<template>
  <div>
    <!-- Back nav -->
    <button @click="$router.push('/quality-control')" class="flex items-center text-sm text-custom-blue mb-5 hover:underline">
      <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
      </svg>
      Back to Quality Control
    </button>

    <div v-if="loading" class="flex justify-center py-16">
      <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-custom-yellow"></div>
    </div>

    <template v-else-if="lot">
      <!-- Lot info card -->
      <div class="mb-6 p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
        <div class="flex items-center justify-between mb-3">
          <h1 class="text-xl font-bold text-custom-blue">
            {{ lot.lab_analyte?.name }}
            <span class="ml-2 text-sm px-2 py-0.5 rounded-full" :class="levelClass(lot.level)">{{ lot.level }}</span>
          </h1>
          <span class="px-3 py-1 rounded-full text-sm"
            :class="lot.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'">
            {{ lot.active ? 'Active' : 'Inactive' }}
          </span>
        </div>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-700">
          <div><span class="text-xs text-gray-400 block">Lot Number</span>{{ lot.lot_number }}</div>
          <div><span class="text-xs text-gray-400 block">Manufacturer</span>{{ lot.manufacturer || '—' }}</div>
          <div><span class="text-xs text-gray-400 block">Mean ± SD</span>{{ lot.mean }} ± {{ lot.sd }} {{ lot.unit }}</div>
          <div><span class="text-xs text-gray-400 block">Expiry</span>{{ lot.expiry_date || '—' }}</div>
          <div><span class="text-xs text-gray-400 block">±1SD range</span>{{ sdRange(1) }}</div>
          <div><span class="text-xs text-gray-400 block">±2SD range</span>{{ sdRange(2) }}</div>
          <div><span class="text-xs text-gray-400 block">±3SD range</span>{{ sdRange(3) }}</div>
          <div><span class="text-xs text-gray-400 block">Discipline</span>{{ lot.lab_analyte?.discipline }}</div>
        </div>
      </div>

      <!-- Results table -->
      <div class="flex justify-between items-center mb-3">
        <h2 class="text-sm font-semibold text-gray-600">QC Results ({{ results.length }})</h2>
        <button @click="showForm = !showForm"
          class="px-4 py-2 text-sm bg-custom-yellow text-white rounded hover:bg-orange-600">
          {{ showForm ? 'Cancel' : '+ Enter Result' }}
        </button>
      </div>

      <!-- Inline entry form -->
      <div v-if="showForm" class="mb-5 p-5 border border-gray-200 rounded-lg bg-gray-50">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Value *</label>
            <input v-model.number="form.value" type="number" step="0.0001" placeholder="0.0000"
              class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Run Date *</label>
            <input v-model="form.run_date" type="date"
              class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Shift</label>
            <select v-model="form.shift" class="w-full border border-gray-300 rounded p-2 text-sm bg-white">
              <option value="">— none —</option>
              <option value="morning">Morning</option>
              <option value="afternoon">Afternoon</option>
              <option value="night">Night</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Comment</label>
            <input v-model="form.comment" type="text" placeholder="Optional…"
              class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
          </div>
        </div>

        <!-- Last result feedback -->
        <div v-if="lastResult" class="mt-3 p-3 rounded border text-sm"
          :class="lastResult.accepted ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'">
          <span class="font-semibold" :class="lastResult.accepted ? 'text-green-700' : 'text-red-700'">
            {{ lastResult.accepted ? 'Accepted' : 'Rejected' }}
          </span>
          — Value: {{ lastResult.value }}, z = {{ zScore(lastResult.value) }}
          <span v-for="flag in lastResult.westgard_flags" :key="flag"
            class="ml-1 px-2 py-0.5 rounded-full text-xs"
            :class="flag === '1_2s' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'">
            {{ flag }}
          </span>
        </div>

        <div class="flex justify-end mt-3">
          <button @click="submitResult" :disabled="saving"
            class="px-5 py-2 text-sm bg-custom-yellow text-white rounded hover:bg-orange-600 disabled:opacity-50">
            {{ saving ? 'Saving…' : 'Record' }}
          </button>
        </div>
      </div>

      <div v-if="resultsLoading" class="flex justify-center py-10">
        <div class="animate-spin rounded-full h-8 w-8 border-t-4 border-custom-yellow"></div>
      </div>

      <div v-else class="overflow-x-auto shadow-sm rounded-sm">
        <table class="w-full text-left text-gray-700 text-sm">
          <thead>
            <tr class="bg-gray-100">
              <th class="py-3 px-4">#</th>
              <th class="py-3 px-4">Date</th>
              <th class="py-3 px-4">Shift</th>
              <th class="py-3 px-4">Value</th>
              <th class="py-3 px-4">z-score</th>
              <th class="py-3 px-4">Flags</th>
              <th class="py-3 px-4">Status</th>
              <th class="py-3 px-4">Comment</th>
            </tr>
          </thead>
          <tbody class="font-light text-gray-600">
            <tr v-if="results.length === 0">
              <td colspan="8" class="py-6 text-center text-gray-400">No results recorded for this lot yet.</td>
            </tr>
            <tr v-for="(r, idx) in results" :key="r.id" class="border-b border-gray-200 hover:bg-gray-50">
              <td class="py-3 px-4">{{ idx + 1 }}</td>
              <td class="py-3 px-4">{{ r.run_date }}</td>
              <td class="py-3 px-4">{{ r.shift || '—' }}</td>
              <td class="py-3 px-4 font-medium">{{ r.value }}</td>
              <td class="py-3 px-4" :class="zScoreClass(zScore(r.value))">{{ zScore(r.value) }}</td>
              <td class="py-3 px-4">
                <span v-if="!r.westgard_flags?.length" class="text-gray-400 text-xs">—</span>
                <span v-for="flag in r.westgard_flags" :key="flag"
                  class="inline-block mr-1 px-2 py-0.5 rounded-full text-xs"
                  :class="flag === '1_2s' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'">
                  {{ flag }}
                </span>
              </td>
              <td class="py-3 px-4">
                <span class="px-2 py-0.5 rounded-full text-xs"
                  :class="r.accepted ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                  {{ r.accepted ? 'Accepted' : 'Rejected' }}
                </span>
              </td>
              <td class="py-3 px-4 text-gray-400">{{ r.comment || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <div v-else class="py-16 text-center text-gray-400">QC lot not found.</div>
  </div>
  <LevyJenningsChart v-if="results.length" :lot="lot" :results="results" />
</template>

<script>
import apiClient from '@/services/api'
import LevyJenningsChart from '@/components/lab/LevyJenningsChart.vue'

export default {
  name: 'QcLotDetailsPage',
  components: {
    LevyJenningsChart
  },
  data() {
    const today = new Date().toISOString().split('T')[0]
    return {
      lot:            null,
      loading:        true,
      results:        [],
      resultsLoading: false,
      showForm:       false,
      saving:         false,
      lastResult:     null,
      form: { qc_lot_id: '', value: '', run_date: today, shift: '', comment: '' },
    }
  },

  async mounted() {
    const lotId = this.$route.params.id
    await this.loadLot(lotId)
    await this.loadResults(lotId)
    this.form.qc_lot_id = lotId
  },

  methods: {
    async loadLot(id) {
      this.loading = true
      try {
        const res = await apiClient.listQcLots()
        if (res.success) this.lot = res.data.find(l => l.id === id) || null
      } catch {
        this.$toast.error('Failed to load lot details')
      } finally {
        this.loading = false
      }
    },

    async loadResults(id) {
      this.resultsLoading = true
      try {
        const res = await apiClient.listQcResults({ qc_lot_id: id })
        if (res.success && Array.isArray(res.data)) this.results = [...res.data].reverse()
      } catch {
        this.$toast.error('Failed to load results')
      } finally {
        this.resultsLoading = false
      }
    },

    async submitResult() {
      if (this.form.value === '' || !this.form.run_date) {
        this.$toast.error('Value and run date are required')
        return
      }
      this.saving = true
      try {
        const res = await apiClient.createQcResult(this.form)
        if (res.success) {
          this.lastResult   = res.data
          this.form.value   = ''
          this.form.comment = ''
          await this.loadResults(this.$route.params.id)
          if (!res.data.accepted) {
            this.$toast.error('Result recorded — Westgard violation')
          } else {
            this.$toast.success('Result recorded — Accepted')
          }
        } else {
          this.$toast.error(res.message || 'Failed to save')
        }
      } catch (e) {
        this.$toast.error(e.message || 'Failed to save result')
      } finally {
        this.saving = false
      }
    },

    zScore(value) {
      if (!this.lot || parseFloat(this.lot.sd) === 0) return '—'
      return ((parseFloat(value) - parseFloat(this.lot.mean)) / parseFloat(this.lot.sd)).toFixed(2)
    },

    zScoreClass(z) {
      if (z === '—') return 'text-gray-400'
      const abs = Math.abs(parseFloat(z))
      if (abs > 3) return 'font-bold text-red-600'
      if (abs > 2) return 'font-semibold text-orange-500'
      return 'text-gray-700'
    },

    sdRange(multiplier) {
      if (!this.lot) return '—'
      const m = parseFloat(this.lot.mean)
      const s = parseFloat(this.lot.sd) * multiplier
      return `${(m - s).toFixed(4)} – ${(m + s).toFixed(4)}`
    },

    levelClass(level) {
      switch (level) {
        case 'low':    return 'bg-blue-100 text-blue-800'
        case 'normal': return 'bg-green-100 text-green-800'
        case 'high':   return 'bg-red-100 text-red-800'
        default:       return 'bg-gray-100 text-gray-600'
      }
    },
  },
}
</script>

<style lang="css" scoped></style>
